import { FC, useMemo, useState } from "react";
import { Grid, Card, Box, Typography, Skeleton } from "@mui/material";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceDot
} from "recharts";
import moment from "moment";
import { MonitoringParameter, TelemetryData } from "app/data/types";
import { BatteryChartsIcon } from "assets/icons";

const SYNC_POINTER_COLOR = "#00B2A3";
const CLICKED_POINTER_COLOR = "#F59E0B";
/** Reference for average cell temperature chart (CEO: compare vs ideal) */
const IDEAL_AVG_CELL_TEMP_C = 12;

const normalizeParamName = (name: string) =>
  name.replace(/[_\s-]/g, "").toLowerCase();

const formatNumberByLocale = (value: number | string): string | number => {
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return value;
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
    maximumFractionDigits: 2
  }).format(numeric);
};

const formatOneDecimalLocale = (value: number | string): string | number => {
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return value;
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }).format(numeric);
};

const getSocScaleFactor = (history: { value: number | string }[]): number => {
  const nums = history
    .map((p) => (typeof p.value === "number" ? p.value : Number(p.value)))
    .filter((n) => Number.isFinite(n));
  if (!nums.length) return 1;
  const maxVal = Math.max(...nums);
  return maxVal > 100 ? 0.01 : 1;
};

const scaleHistoryValues = (
  history: { timestamp: string; value: number | string }[],
  factor: number
) =>
  history.map((row) => {
    const v = typeof row.value === "number" ? row.value : Number(row.value);
    return {
      ...row,
      value: Number.isFinite(v) ? v * factor : row.value
    };
  });

const getLayoutEnumsForParam = (param: MonitoringParameter) => {
  const normalized = normalizeParamName(param.name);

  if (normalized === "regenlevel") {
    return {
      "0": "No regen",
      "1": "Low",
      "2": "Medium",
      "3": "High",
      "4": "Max"
    };
  }

  if (normalized === "immpowermode" || normalized === "inmpowermodeactive") {
    return { "1": "Inactive", "2": "Active" };
  }

  if (normalized === "desmode") {
    return {
      "1": "Work mode 1",
      "2": "Work mode 2",
      "3": "Work mode 3",
      "4": "Work mode 4"
    };
  }

  if (normalized === "regenstat") {
    return { "0": "No regen", "1": "Manual", "2": "Auto" };
  }

  return param.enums;
};

function findPointAtTimestamp(
  history: { timestamp: string; value: number | string }[],
  ts: string | null
): { timestamp: string; value: number | string } | null {
  if (!ts || !history?.length) return null;
  const exact = history.find((h) => h.timestamp === ts);
  if (exact) return exact;
  const targetMs = moment(ts).valueOf();
  let best = history[0];
  let bestDiff = Infinity;
  for (const h of history) {
    const d = Math.abs(moment(h.timestamp).valueOf() - targetMs);
    if (d < bestDiff) {
      bestDiff = d;
      best = h;
    }
  }
  return best;
}

interface Props {
  parameters: MonitoringParameter[];
  telemetry: TelemetryData[];
  loading: boolean;
  rangeFrom: string;
  rangeTo: string;
  syncHover: boolean;
  hoveredTimestamp: string | null;
  clickedTimestamp: string | null;
  setHoveredTimestamp: (ts: string | null) => void;
  onToggleClickTimestamp: (ts: string) => void;
}

const ChartCard: FC<{
  param: MonitoringParameter;
  data?: TelemetryData;
  rangeFrom: string;
  rangeTo: string;
  syncHover: boolean;
  hoveredTimestamp: string | null;
  clickedTimestamp: string | null;
  onHover: (ts: string | null) => void;
  onClickPoint: (ts: string) => void;
}> = ({
  param,
  data,
  rangeFrom,
  rangeTo,
  syncHover,
  hoveredTimestamp,
  clickedTimestamp,
  onHover,
  onClickPoint
}) => {
  const [localHoveredTimestamp, setLocalHoveredTimestamp] = useState<
    string | null
  >(null);
  const [localClickedTimestamp, setLocalClickedTimestamp] = useState<
    string | null
  >(null);

  // If no data exists for this param or it has an empty array
  const hasData = data && data.history && data.history.length > 0;
  const paramNorm = normalizeParamName(param.name);
  const isSoc = paramNorm === "soc";
  const isAvgCellTemp =
    paramNorm === "avgcelltemp" || paramNorm === "averagecelltemperature";

  const displayData = useMemo(() => {
    const start = moment(rangeFrom);
    const end = moment(rangeTo);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return [];

    if (hasData) {
      let sortedHistory = [...(data?.history ?? [])].sort(
        (a, b) => moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf()
      );

      if (isSoc && sortedHistory.length) {
        const factor = getSocScaleFactor(sortedHistory);
        sortedHistory = scaleHistoryValues(sortedHistory, factor);
      }

      const firstTs = sortedHistory[0]?.timestamp;
      const lastTs = sortedHistory[sortedHistory.length - 1]?.timestamp;

      const withBounds: { timestamp: string; value: number | string }[] = [];
      const firstValue = sortedHistory[0]?.value ?? Number.NaN;
      const lastValue =
        sortedHistory[sortedHistory.length - 1]?.value ?? Number.NaN;
      if (!firstTs || !moment(firstTs).isSame(start)) {
        withBounds.push({ timestamp: start.toISOString(), value: firstValue });
      }
      withBounds.push(...sortedHistory);
      if (!lastTs || !moment(lastTs).isSame(end)) {
        withBounds.push({ timestamp: end.toISOString(), value: lastValue });
      }
      return withBounds;
    }

    const durationMins = Math.max(
      1,
      Math.round(moment.duration(end.diff(start)).asMinutes())
    );
    let stepMins = 5;
    if (durationMins > 60 * 24 * 7) stepMins = 60 * 24;
    else if (durationMins > 60 * 24) stepMins = 60;
    else if (durationMins > 60 * 6) stepMins = 15;

    const timeline: { timestamp: string; value: number }[] = [];
    let cursor = start.clone();
    while (cursor.isBefore(end) || cursor.isSame(end)) {
      timeline.push({ timestamp: cursor.toISOString(), value: Number.NaN });
      cursor = cursor.add(stepMins, "minutes");
    }
    if (
      !timeline.length ||
      !moment(timeline[timeline.length - 1].timestamp).isSame(end)
    ) {
      timeline.push({ timestamp: end.toISOString(), value: Number.NaN });
    }
    return timeline;
  }, [hasData, data?.history, rangeFrom, rangeTo, isSoc]);
  const xAxisDateFormat = useMemo(() => {
    const start = moment(rangeFrom);
    const end = moment(rangeTo);
    const durationHours = moment.duration(end.diff(start)).asHours();
    if (durationHours > 24 * 7) return "DD/MM";
    if (durationHours > 24) return "DD/MM HH:mm";
    return "HH:mm";
  }, [rangeFrom, rangeTo]);

  const activeClickedTimestamp = syncHover
    ? clickedTimestamp
    : localClickedTimestamp;

  const effectiveEnums = useMemo(() => getLayoutEnumsForParam(param), [param]);
  const enumTickValues = useMemo(() => {
    if (param.type !== "enum" || !effectiveEnums) return undefined;
    return Object.keys(effectiveEnums)
      .map(Number)
      .filter((value) => Number.isFinite(value))
      .sort((a, b) => a - b);
  }, [param.type, effectiveEnums]);
  const yAxisDomain = useMemo(() => {
    if (param.type === "enum" && enumTickValues && enumTickValues.length > 0) {
      const min = enumTickValues[0];
      const max = enumTickValues[enumTickValues.length - 1];
      return [min, max] as [number, number];
    }
    if (isSoc) {
      return [0, 100] as [number, number];
    }
    if (isAvgCellTemp && data?.history?.length) {
      const nums = data.history
        .map((p) => (typeof p.value === "number" ? p.value : Number(p.value)))
        .filter((n) => Number.isFinite(n));
      if (nums.length) {
        const dMin = Math.min(...nums);
        const dMax = Math.max(...nums);
        const spread = dMax - dMin;
        const pad = Math.max(1, spread > 0 ? spread * 0.12 : 2);
        const min = Math.min(dMin, IDEAL_AVG_CELL_TEMP_C) - pad;
        const max = Math.max(dMax, IDEAL_AVG_CELL_TEMP_C) + pad;
        return [min, max] as [number, number];
      }
    }
    return [param.scale?.min ?? "auto", param.scale?.max ?? "auto"] as [
      number | "auto",
      number | "auto"
    ];
  }, [
    param.type,
    enumTickValues,
    param.scale?.min,
    param.scale?.max,
    isSoc,
    isAvgCellTemp,
    data?.history
  ]);
  const yAxisWidth = param.type === "enum" ? 92 : 56;

  // Format current value based on enum / SoC / temperature
  let currentValueDisplay: string | number = data?.currentValue ?? "-";
  if (
    param.type === "enum" &&
    effectiveEnums &&
    data?.currentValue !== undefined
  ) {
    currentValueDisplay =
      effectiveEnums[data.currentValue.toString()] ?? data.currentValue;
  } else if (
    isSoc &&
    data?.currentValue !== undefined &&
    data?.currentValue !== null
  ) {
    const raw =
      typeof data.currentValue === "number"
        ? data.currentValue
        : Number(data.currentValue);
    if (Number.isFinite(raw)) {
      const factor = data.history?.length
        ? getSocScaleFactor(data.history)
        : raw > 100
          ? 0.01
          : 1;
      const pct = raw * factor;
      currentValueDisplay = formatNumberByLocale(pct);
    }
  } else if (
    isAvgCellTemp &&
    data?.currentValue !== undefined &&
    data?.currentValue !== null
  ) {
    currentValueDisplay = formatOneDecimalLocale(data.currentValue);
  } else if (data?.currentValue !== undefined && data?.currentValue !== null) {
    currentValueDisplay = formatNumberByLocale(data.currentValue);
  }

  // Handle syncing tooltip
  const handleMouseMove = (e: any) => {
    if (!hasData) return;
    const ts = e?.activePayload?.[0]?.payload?.timestamp ?? e?.activeLabel;
    if (!ts) return;

    if (syncHover) {
      onHover(ts);
    } else {
      setLocalHoveredTimestamp(ts);
    }
  };

  const handleMouseLeave = () => {
    if (!syncHover) {
      setLocalHoveredTimestamp(null);
    }
  };

  const handleChartClick = (e: any) => {
    if (!hasData) return;
    const timestamp =
      e?.activePayload?.[0]?.payload?.timestamp ?? e?.activeLabel;
    if (!timestamp) return;
    if (syncHover) {
      onClickPoint(timestamp);
    } else {
      setLocalClickedTimestamp((current) =>
        current === timestamp ? null : timestamp
      );
    }
  };

  const activeHoveredTimestamp = syncHover
    ? hoveredTimestamp
    : localHoveredTimestamp;
  const hoveredPoint = useMemo(() => {
    if (!activeHoveredTimestamp || !displayData.length) return null;
    return findPointAtTimestamp(displayData, activeHoveredTimestamp);
  }, [activeHoveredTimestamp, displayData]);
  const hoveredY = useMemo(() => {
    if (!hoveredPoint) return null;
    const v = hoveredPoint.value;
    if (typeof v === "number" && !Number.isNaN(v)) return v;
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  }, [hoveredPoint]);

  const formatTooltipValue = (raw: number | string) => {
    if (param.type === "enum" && effectiveEnums) {
      return effectiveEnums[raw.toString()] ?? raw;
    }
    if (isSoc) {
      const n = typeof raw === "number" ? raw : Number(raw);
      if (!Number.isFinite(n)) return raw;
      return `${formatNumberByLocale(n)}%`;
    }
    if (isAvgCellTemp) {
      return formatOneDecimalLocale(raw);
    }
    return formatNumberByLocale(raw);
  };

  return (
    <Card
      variant="outlined"
      sx={{ p: 2, borderRadius: "8px", borderColor: "#D5D9E8" }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
      >
        <Box>
          <Typography
            variant="body2"
            sx={{ color: "#646D7B", fontWeight: 500 }}
          >
            {param.name}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#1D1F2B" }}>
            {currentValueDisplay}
            {param.unit}
          </Typography>
        </Box>
        <BatteryChartsIcon
          style={{ width: 16, height: 16, flexShrink: 0, color: "#94A3B8" }}
        />
      </Box>

      <Box sx={{ width: "100%", height: 200, mt: 3 }}>
        {!hasData && (
          <Box
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography color="textSecondary">
              No data available for this period.
            </Typography>
          </Box>
        )}
        <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={displayData}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={handleChartClick}
            >
              <defs>
                <linearGradient
                  id={`color-${param.id}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#8A56FA" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#8A56FA" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="timestamp"
                tickFormatter={(ts) => `${moment(ts).format(xAxisDateFormat)}`}
                axisLine={false}
                tickLine={false}
                minTickGap={30}
                interval="preserveStartEnd"
                stroke="#94A3B8"
                tick={{ fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                stroke="#94A3B8"
                width={yAxisWidth}
                tick={{ fontSize: 12 }}
                tickMargin={12}
                ticks={enumTickValues}
                interval={0}
                domain={yAxisDomain}
                tickFormatter={(val) => {
                  if (param.type === "enum" && effectiveEnums) {
                    const label = `${effectiveEnums[val.toString()] ?? val}`;
                    return label.replace(/\s+/g, "\u00A0");
                  }
                  if (isAvgCellTemp) {
                    return `${formatOneDecimalLocale(val)}`;
                  }
                  return `${formatNumberByLocale(val)}`;
                }}
              />
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#E2E8F0"
              />

              {isAvgCellTemp && (
                <ReferenceLine
                  y={IDEAL_AVG_CELL_TEMP_C}
                  stroke="#94A3B8"
                  strokeWidth={1}
                  strokeDasharray="4 4"
                  label={{
                    value: `Ideal ${IDEAL_AVG_CELL_TEMP_C} °C`,
                    position: "insideTopRight",
                    fill: "#64748B",
                    fontSize: 11
                  }}
                />
              )}

              <Area
                type={param.type === "enum" ? "stepAfter" : "monotone"}
                dataKey="value"
                stroke="#8A56FA"
                strokeWidth={2}
                fillOpacity={1}
                fill={`url(#color-${param.id})`}
                isAnimationActive={false}
                activeDot={{
                  r: 6,
                  fill: "#FFF",
                  stroke: "#00B2A3",
                  strokeWidth: 2
                }}
              />

              {hoveredPoint && (
                <ReferenceLine
                  x={hoveredPoint.timestamp}
                  stroke={SYNC_POINTER_COLOR}
                  strokeWidth={2}
                />
              )}
              {hoveredPoint && hoveredY !== null && (
                <ReferenceDot
                  x={hoveredPoint.timestamp}
                  y={hoveredY}
                  r={0}
                  fill="transparent"
                  stroke="none"
                  ifOverflow="visible"
                  shape={(dotProps: { cx?: number; cy?: number }) => {
                    const cx = dotProps.cx ?? 0;
                    const cy = dotProps.cy ?? 0;
                    const boxH = 44;
                    const gap = 8;
                    const timeStr = moment(hoveredPoint.timestamp).format(
                      "HH:mm"
                    );
                    const valStr = isSoc
                      ? formatTooltipValue(hoveredPoint.value)
                      : `${formatTooltipValue(hoveredPoint.value)}${
                          param.unit ? ` ${param.unit}` : ""
                        }`;
                    const valStrLen = String(valStr).length;
                    const minW = 60;
                    const maxW = 160;
                    const estimatedW = Math.ceil(
                      Math.max(timeStr.length * 6.5, valStrLen * 7) + 28
                    );
                    const boxW = Math.max(minW, Math.min(maxW, estimatedW));
                    const fx = cx - boxW / 2;
                    const fyAbove = cy - boxH - gap;
                    const fyBelow = cy + gap;
                    const fy = fyAbove < 4 ? fyBelow : fyAbove;
                    return (
                      <g style={{ pointerEvents: "none" }}>
                        <circle
                          cx={cx}
                          cy={cy}
                          r={5}
                          fill="#fff"
                          stroke={SYNC_POINTER_COLOR}
                          strokeWidth={2}
                        />
                        <foreignObject
                          x={fx}
                          y={fy}
                          width={boxW}
                          height={boxH}
                          style={{ overflow: "visible" }}
                        >
                          <div
                            {...({
                              xmlns: "http://www.w3.org/1999/xhtml"
                            } as Record<string, unknown>)}
                            style={{
                              background: "#fff",
                              border: "1px solid #E2E8F0",
                              borderRadius: 4,
                              boxShadow:
                                "0px 1px 3px rgba(0,0,0,0.12), 0px 1px 2px rgba(0,0,0,0.08)",
                              padding: 8,
                              fontFamily: "system-ui, sans-serif",
                              boxSizing: "border-box",
                              lineHeight: 1.2,
                              minWidth: minW,
                              width: "100%"
                            }}
                          >
                            <div
                              style={{
                                color: "rgba(0, 0, 0, 0.6)",
                                fontSize: 12,
                                lineHeight: 1.2
                              }}
                            >
                              {timeStr}
                            </div>
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: 14,
                                color: "#1D1F2B",
                                lineHeight: 1.25,
                                marginTop: 2
                              }}
                            >
                              {valStr}
                            </div>
                          </div>
                        </foreignObject>
                      </g>
                    );
                  }}
                />
              )}
              {activeClickedTimestamp && (
                <ReferenceLine
                  x={activeClickedTimestamp}
                  stroke={CLICKED_POINTER_COLOR}
                  strokeWidth={2}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Card>
  );
};

const TelemetryChartsGrid: FC<Props> = ({
  parameters,
  telemetry,
  loading,
  rangeFrom,
  rangeTo,
  syncHover,
  hoveredTimestamp,
  clickedTimestamp,
  setHoveredTimestamp,
  onToggleClickTimestamp
}) => {
  const kpiOnlyFieldNames = new Set([
    "crdhours",
    "motorhours",
    "inverterhours",
    "desmode",
    "inmpowermodeactive",
    "immpowermode",
    "systemstate",
    "sysfailurestate",
    "systemfailurestate"
  ]);
  const activeParameters = parameters.filter((param) => {
    if (param.isActiveField === false) return false;
    const normalizedName = param.name.replace(/[_\s-]/g, "").toLowerCase();
    return !kpiOnlyFieldNames.has(normalizedName);
  });
  const visibleParameters = activeParameters.filter((param) => {
    const data = telemetry.find((t) => t.parameterId === param.id);
    return Boolean(data?.history?.length);
  });
  const hasAnyChartData = visibleParameters.some((param) => {
    const data = telemetry.find((t) => t.parameterId === param.id);
    return Boolean(data?.history?.length);
  });

  if (loading) {
    return (
      <Grid container spacing={3} mb={4}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} lg={6} key={i}>
            <Skeleton variant="rounded" height={300} width="100%" />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!loading && (!activeParameters.length || !hasAnyChartData)) {
    return (
      <Grid container spacing={3} mb={4}>
        {[1, 2].map((i) => (
          <Grid item xs={12} lg={6} key={`empty-${i}`}>
            <Card
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: "8px",
                borderColor: "#D5D9E8",
                overflow: "hidden"
              }}
            >
              <Box sx={{ mb: 2, filter: "blur(2px)", opacity: 0.55 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#646D7B", fontWeight: 500 }}
                >
                  Chart
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "#1D1F2B" }}
                >
                  --
                </Typography>
              </Box>
              <Box
                sx={{ height: 200, position: "relative", overflow: "hidden" }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    filter: "blur(2px)",
                    opacity: 0.6
                  }}
                >
                  <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 100 40"
                    preserveAspectRatio="none"
                  >
                    <polyline
                      points="0,30 12,22 24,20 36,18 48,16 60,15 72,13 84,10 100,7"
                      fill="none"
                      stroke="#8A56FA"
                      strokeWidth="1.5"
                    />
                  </svg>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#646D7B",
                    fontWeight: 500,
                    textAlign: "center",
                    px: 2
                  }}
                >
                  No data available for this period.
                </Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} mb={4}>
      {visibleParameters.map((param) => {
        const data = telemetry.find((t) => t.parameterId === param.id);
        return (
          <Grid item xs={12} lg={6} key={param.id}>
            <ChartCard
              param={param}
              data={data}
              rangeFrom={rangeFrom}
              rangeTo={rangeTo}
              syncHover={syncHover}
              hoveredTimestamp={hoveredTimestamp}
              clickedTimestamp={clickedTimestamp}
              onHover={setHoveredTimestamp}
              onClickPoint={onToggleClickTimestamp}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default TelemetryChartsGrid;
